import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { first } from 'rxjs';
import { AiOrderParserConfig } from '../../../models/ai-order-parser-config';
import { PdfAiParserService } from '../../../services/pdf-ai-parser/pdf-ai-parser.service';
import { CustomerService } from '../../../services/customers/customer.service';

@Component({
  selector: 'lib-pdf-ai-parser-config-list',
  standalone: false,
  templateUrl: './pdf-ai-parser-config-list.component.html',
  styleUrl: './pdf-ai-parser-config-list.component.scss'
})
export class PdfAiParserConfigListComponent implements OnInit {
  @ViewChild('deleteModal') deleteModalTpl!: TemplateRef<unknown>;

  configs: AiOrderParserConfig[] = [];
  loading = true;
  errorMessage = '';
  searchTerm = '';
  /** Page-size options mirror the order inbox's "Visa N" selector. */
  readonly pageSizes = [25, 50, 100];
  pageSize = 25;
  /** The config shown in the delete-confirm dialog, so its name can be interpolated in the prompt. */
  configToDelete: AiOrderParserConfig | null = null;

  /** Resolved customer names keyed by customer id; '' when the id resolved to nothing. */
  customerNames = new Map<string, string>();

  private pdfAiParserService = inject(PdfAiParserService);
  private customerService = inject(CustomerService);
  private modalService = inject(NgbModal);
  private router = inject(Router);

  ngOnInit(): void {
    this.load();
  }

  /** Configs filtered by the search term, matching on name and delivery/billing customer id. */
  get visibleConfigs(): AiOrderParserConfig[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.configs;
    }
    return this.configs.filter(config =>
      (config.name || '').toLowerCase().includes(term)
      || (config.suggestedDeliveryCustomerId || '').toLowerCase().includes(term)
      || (config.suggestedBillingCustomerId || '').toLowerCase().includes(term)
    );
  }

  /** The filtered configs limited to the selected page size. */
  get pagedConfigs(): AiOrderParserConfig[] {
    return this.visibleConfigs.slice(0, this.pageSize);
  }

  load(): void {
    this.loading = true;
    this.pdfAiParserService.listConfigs().pipe(first()).subscribe({
      next: configs => {
        this.configs = [...configs].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        this.resolveCustomerNames();
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'AI_PARSER_ERROR_LOAD_CONFIGS';
        this.loading = false;
      }
    });
  }

  create(): void {
    this.router.navigate(['/order-parser/configs/new']);
  }

  edit(config: AiOrderParserConfig): void {
    if (!config.id) {
      return;
    }
    this.router.navigate(['/order-parser/configs', config.id]);
  }

  askDelete(config: AiOrderParserConfig): void {
    if (!config.id) {
      return;
    }
    this.configToDelete = config;
    this.modalService.open(this.deleteModalTpl, { centered: true }).result.then(
      () => {
        this.performDelete(config);
        this.configToDelete = null;
      },
      () => {
        this.configToDelete = null;
      }
    );
  }

  private performDelete(config: AiOrderParserConfig): void {
    if (!config.id) {
      return;
    }
    this.pdfAiParserService.deleteConfig(config.id).pipe(first()).subscribe({
      next: () => {
        this.configs = this.configs.filter(c => c.id !== config.id);
      },
      error: () => {
        this.errorMessage = 'AI_PARSER_ERROR_DELETE_CONFIG';
      }
    });
  }

  customerName(customerId?: string): string | null {
    if (!customerId) {
      return null;
    }
    return this.customerNames.get(customerId) || null;
  }

  /** True while a customer id's name lookup is still in flight, so the row can show a skeleton. */
  isResolvingCustomer(customerId?: string): boolean {
    return !!customerId && !this.customerNames.has(customerId);
  }

  /** True once a customer id resolved but matched no customer, so the row can flag it. */
  customerNotFound(customerId?: string): boolean {
    return !!customerId && this.customerNames.has(customerId) && !this.customerNames.get(customerId);
  }

  /** A one-line, character-capped preview of the AI instructions for the row. */
  promptPreview(instructions?: string): string {
    const max = 120;
    const clean = (instructions || '').replace(/\s+/g, ' ').trim();
    return clean.length > max ? clean.slice(0, max).trimEnd() + '…' : clean;
  }

  private resolveCustomerNames(): void {
    const ids = new Set<string>();
    for (const config of this.configs) {
      if (config.suggestedDeliveryCustomerId) {
        ids.add(config.suggestedDeliveryCustomerId);
      }
      if (config.suggestedBillingCustomerId) {
        ids.add(config.suggestedBillingCustomerId);
      }
    }
    for (const id of ids) {
      if (this.customerNames.has(id)) {
        continue;
      }
      this.customerService.getCustomer(id).pipe(first()).subscribe({
        next: customer => this.customerNames.set(id, customer?.name || ''),
        error: () => this.customerNames.set(id, '')
      });
    }
  }
}
